import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { EmployeeMultiSelect } from '../employee-multi-select/employee-multi-select';
import { useCreateProject, useUpdateProject } from '../../hooks/use-projects';
import { projectFormSchema, ProjectFormValues } from '../../schemas/project-form-schema';
import { Project } from '../../types/project.types';

export interface ProjectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: Project | null;
}

export const ProjectFormDialog = ({ open, onOpenChange, project }: ProjectFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(project);

  const { mutateAsync: createProject, isPending: isCreating } = useCreateProject();
  const { mutateAsync: updateProject, isPending: isUpdating } = useUpdateProject();
  const isSubmitting = isCreating || isUpdating;

  // LeadNames/MemberNames are denormalized, comma-separated display strings
  // (real schema fields, not arrays) — kept in sync with the id selections
  // via EmployeeMultiSelect's onSelectedNamesChange.
  const [leadNames, setLeadNames] = useState('');
  const [memberNames, setMemberNames] = useState('');

  const form = useForm<ProjectFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(projectFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { ProjectName: '', Leads: [], Members: [] },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      ProjectName: project?.ProjectName ?? '',
      Leads: project?.Leads ?? [],
      Members: project?.Members ?? [],
    });
    setLeadNames(project?.LeadNames ?? '');
    setMemberNames(project?.MemberNames ?? '');
  }, [open, project, form]);

  const leads = form.watch('Leads');
  const members = form.watch('Members');

  const handleSubmit = async (values: ProjectFormValues) => {
    const payload = { ...values, LeadNames: leadNames, MemberNames: memberNames };
    try {
      if (isEditMode && project) {
        await updateProject({ itemId: project.ItemId, input: payload });
      } else {
        await createProject(payload);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_PROJECT') : t('CREATE_PROJECT')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="ProjectName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('PROJECT_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('PROJECT_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Leads"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('LEADS')}</FormLabel>
                  <FormControl>
                    <EmployeeMultiSelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('SELECT_LEADS')}
                      excludeIds={members}
                      onSelectedNamesChange={(names) => setLeadNames(names.join(', '))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Members"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('MEMBERS')}</FormLabel>
                  <FormControl>
                    <EmployeeMultiSelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('SELECT_MEMBERS')}
                      excludeIds={leads}
                      onSelectedNamesChange={(names) => setMemberNames(names.join(', '))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
